import { LearningTemplatePackageBase } from "@lib/Api";
import { superOwnerAppLearningTemplatePackagesState } from "@reducers/superOwnerApp/templatePackages/learningTemplatePackage";
import {
  useGetLearningTemplatePackagesFrameAsSuperOwnerQuery,
  useGetPackagingsAsSuperOwnerQuery,
} from "@root/endpoints/TimeTactApi/superOwnerApi";
import { useAppSelector } from "@root/hooks";
import { useMemo } from "react";

type Props = {
  openModule: boolean;
  learningTemplate: LearningTemplatePackageBase;
};

export const useAccordionContentsService = ({
  openModule,
  learningTemplate,
}: Props) => {
  const {
    data: packagingData,
    isLoading: isLoadingPackaging,
    isSuccess: isSuccessPackaging,
    refetch: refetchPackaging,
  } = useGetPackagingsAsSuperOwnerQuery(
    {
      perPage: 100,
      packageId: learningTemplate.id,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !openModule || learningTemplate.has_frames,
    },
  );

  const {
    data: packageInFrameData,
    isLoading: isLoadingPackageInFrame,
    isSuccess: isSuccessPackageInFrame,
    refetch: refetchPackageInFrame,
  } = useGetLearningTemplatePackagesFrameAsSuperOwnerQuery(
    {
      learningTemplatePackageId: learningTemplate.id,
      perPage: 100,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !openModule || !learningTemplate.has_frames,
    },
  );

  const packages = useMemo(
    () => packagingData?.packagings ?? [],
    [packagingData],
  );
  const packageInFrame = useMemo(
    () => packageInFrameData?.learning_template_package_frames ?? [],
    [packageInFrameData],
  );
  const isLoading = useMemo(
    () =>
      !learningTemplate.has_frames
        ? isLoadingPackaging
        : isLoadingPackageInFrame,
    [isLoadingPackageInFrame, isLoadingPackaging, learningTemplate.has_frames],
  );
  const isSuccess = useMemo(
    () =>
      !learningTemplate.has_frames
        ? isSuccessPackaging
        : isSuccessPackageInFrame,
    [isSuccessPackageInFrame, isSuccessPackaging, learningTemplate.has_frames],
  );
  const refetch = useMemo(
    () =>
      !learningTemplate.has_frames ? refetchPackaging : refetchPackageInFrame,
    [learningTemplate.has_frames, refetchPackageInFrame, refetchPackaging],
  );
  const packagesArray =
    useMemo(() => {
      const packageFrameItems = packageInFrame?.flatMap(
        (item) => item.learning_template_package_frame_items,
      );
      return learningTemplate.has_frames
        ? packageFrameItems?.map((item) => item.packaging)
        : packages;
    }, [learningTemplate.has_frames, packageInFrame, packages]) ?? [];

  const learningTemplateCache = useAppSelector(
    superOwnerAppLearningTemplatePackagesState,
  );

  return {
    packages,
    packageInFrame,
    isLoading,
    isSuccess,
    refetch,
    packagesArray,
    learningTemplateCache,
  };
};
